import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Wrench } from 'lucide-react';
import { KIND_META } from './osConfig';

// UI-R1 placeholder body. Renders a workspace's contextual sections as cards, each classified
// (ACTION/STATUS/RECORD/DECISION/ANALYTIC) with a link into the existing authoritative surface so
// no functionality is lost before migration. No backend writes here.
export default function WorkspaceView({ workspace, activeSection }) {
  const navigate = useNavigate();
  if (!workspace) return null;
  const section = workspace.sections.find((s) => s.id === activeSection) || workspace.sections[0];

  return (
    <div className="os-animate" data-testid={`os-workspace-${workspace.id}`}>
      {/* Active section spotlight */}
      <div className="os-surface rounded-2xl p-5 sm:p-6 mb-5" data-testid={`os-section-${section.id}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Kind kind={section.kind} />
            <h2 className="os-display text-3xl sm:text-4xl mt-2">{section.label}</h2>
            <p className="text-sm mt-1 max-w-xl" style={{ color: 'var(--os-muted)' }}>{section.note}</p>
          </div>
          {section.link && (
            <button onClick={() => navigate(section.link)} className="os-btn-primary" data-testid={`os-open-${section.id}`}>
              Open current tool <ArrowUpRight className="h-4 w-4" />
            </button>
          )}
        </div>
        {!section.link && (
          <div className="mt-4 flex items-center gap-2 text-sm px-3 py-2 rounded-lg"
               style={{ color: 'var(--os-warning)', background: 'rgba(215,173,85,.12)' }}>
            <Wrench className="h-4 w-4" /> Planned in a later pass — unknown backend truth stays unknown.
          </div>
        )}
      </div>

      {/* All sections in this workspace */}
      <p className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--os-muted)' }}>
        {workspace.title} · {workspace.sections.length} areas
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {workspace.sections.map((s) => (
          <div key={s.id} className="os-card p-4 cursor-pointer" data-testid={`os-card-${s.id}`}
               onClick={() => (s.link ? navigate(s.link) : null)}>
            <div className="flex items-center justify-between">
              <Kind kind={s.kind} />
              {s.link && <ArrowUpRight className="h-4 w-4" style={{ color: 'var(--os-muted)' }} />}
            </div>
            <h3 className="font-bold text-lg mt-2" style={{ color: 'var(--os-text)' }}>{s.label}</h3>
            <p className="text-xs mt-1" style={{ color: 'var(--os-muted)' }}>{s.note}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const Kind = ({ kind }) => {
  const m = KIND_META[kind] || KIND_META.RECORD;
  return <span className="os-chip" style={{ color: m.color, border: `1px solid ${m.color}55` }} data-testid={`os-kind-${kind}`}>{m.label}</span>;
};
